import { Injectable, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { GeocodingService } from './geocoding.service';
import {
  AlarmZone,
  ZoneLocation,
  buildAlarmZonePayload,
  extractZoneErrors,
  hasCoordinates,
  normalizeAddress
} from './alarm-zone.model';

/** Stan pola adresu strefy. Jeden typ zamiast kilku niezależnych flag. */
export type ZoneAddressStatus = 'idle' | 'resolving' | 'notFound' | 'rejected';

/**
 * Edycja strefy alarmowej: stan pola adresu, ustalanie współrzędnych i budowa
 * payloadu. Dostarczany per komponent (`providers: [AlarmZoneEditorService]`),
 * bo trzyma stan jednego formularza.
 *
 * Zasada, na której wszystko stoi: **adres bez współrzędnych nie jest strefą**.
 * Wcześniej wpisanie adresu z ręki, bez wyboru z listy podpowiedzi, zapisywało
 * strefę z punktem 0,0 — czyli geofence na Atlantyku, przez który opaska
 * meldowała wyjście co interwał.
 */
@Injectable()
export class AlarmZoneEditorService implements OnDestroy {

  status: ZoneAddressStatus = 'idle';
  serverErrors: string[] = [];

  private form: FormGroup;
  /** Adres, do którego mamy potwierdzone współrzędne. */
  private confirmedAddress: string = null;
  private destroy$ = new Subject<void>();
  /** Emituje przy każdej zmianie współrzędnych — mapa się na tym centruje. */
  private located$ = new Subject<ZoneLocation>();

  constructor(private geocoding: GeocodingService) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.located$.complete();
  }

  /**
   * Podpina formularz strefy (pola: address, radius, interval, latitude, longitude).
   * Zmiana tekstu adresu unieważnia współrzędne, żeby nowy adres nigdy nie pojechał
   * ze starym punktem — PATCH jest zapisem częściowym i taka rozjazdka byłaby
   * niewidoczna: panel pokazywałby nowy adres, a opaska pilnowała poprzedniego okręgu.
   */
  attach(form: FormGroup) {
    this.form = form;

    // Formularz mógł dostać strefę z API jeszcze przed podpięciem (panel za *ngIf) —
    // wtedy valueChanges niżej już nie wyemituje, a zapisany adres ma swój punkt,
    // więc uznajemy go za potwierdzony od razu.
    if (this.hasCoordinates() && this.addressValue()) {
      this.confirmedAddress = this.addressValue();
    }

    form.get('address').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        if (normalizeAddress(value) === normalizeAddress(this.confirmedAddress)) {
          return;
        }
        form.get('latitude').setValue(null, { emitEvent: false });
        form.get('longitude').setValue(null, { emitEvent: false });
        this.status = 'idle';
        this.serverErrors = [];
      });

    // Współrzędne potrafią przyjść z zewnątrz — rodzic wstawia je patchem po
    // pobraniu pacjenta z API. Wtedy adres z bazy uznajemy za potwierdzony,
    // żeby nie geokodować od nowa czegoś, co ma już swój zapisany punkt.
    ['latitude', 'longitude'].forEach(field => {
      form.get(field).valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.hasCoordinates()) {
            return;
          }
          if (this.confirmedAddress === null && this.addressValue()) {
            this.confirmedAddress = this.addressValue();
          }
          this.located$.next(this.currentLocation());
        });
    });
  }

  /** Strumień lokalizacji do wyśrodkowania mapy. */
  get located(): Observable<ZoneLocation> {
    return this.located$.asObservable();
  }

  get isResolving(): boolean { return this.status === 'resolving'; }
  get isNotFound(): boolean { return this.status === 'notFound'; }

  addressValue(): string {
    return normalizeAddress(this.form.get('address').value);
  }

  hasCoordinates(): boolean {
    return hasCoordinates(this.form.get('latitude').value, this.form.get('longitude').value);
  }

  currentLocation(): ZoneLocation {
    return {
      address: this.addressValue(),
      latitude: parseFloat(this.form.get('latitude').value),
      longitude: parseFloat(this.form.get('longitude').value)
    };
  }

  /** Zapisuje potwierdzoną lokalizację do formularza — z listy podpowiedzi albo z geokodowania. */
  confirm(location: ZoneLocation) {
    this.confirmedAddress = location.address;
    this.form.get('latitude').setValue(location.latitude);
    this.form.get('longitude').setValue(location.longitude);
    this.form.get('address').setValue(location.address, { emitEvent: false });
    this.status = 'idle';
    this.serverErrors = [];
  }

  /**
   * Ustala współrzędne dla ręcznie wpisanego adresu. Emituje `true`, gdy strefa
   * jest gotowa do zapisu — także wtedy, gdy adres jest pusty, bo brak strefy
   * to poprawny stan. Wołane przed każdym zapisem.
   */
  resolve(): Observable<boolean> {
    const address = this.addressValue();

    if (!address) {
      this.status = 'idle';
      return of(true);
    }
    if (this.hasCoordinates() && address === normalizeAddress(this.confirmedAddress)) {
      return of(true);
    }

    this.status = 'resolving';
    return this.geocoding.geocode(address).pipe(
      takeUntil(this.destroy$),
      map(location => {
        if (!location) {
          this.status = 'notFound';
          return false;
        }
        this.confirm(location);
        return true;
      })
    );
  }

  /**
   * Wpisuje strefę do payloadu. Zwraca false, gdy adres jest, a współrzędnych nie —
   * wtedy klucz `zone` nie powstaje w ogóle, bo backend odrzuca niepełną strefę
   * błędem 400, a wysłanie samego adresu zostawiłoby geofence w starym miejscu.
   */
  applyTo(payload: { zone?: AlarmZone | null }): boolean {
    if (!this.addressValue()) {
      // Pusty adres = jawne skasowanie strefy; backend usuwa wiersz na null.
      payload.zone = null;
      return true;
    }
    if (this.hasCoordinates()) {
      payload.zone = buildAlarmZonePayload(this.form.value);
      return true;
    }
    return false;
  }

  /** Przyjmuje błędy walidacji strefy z odpowiedzi backendu. Zwraca false, gdy błąd był o czym innym. */
  handleServerError(err: any): boolean {
    this.serverErrors = extractZoneErrors(err);
    if (this.serverErrors.length) {
      this.status = 'rejected';
      return true;
    }
    return false;
  }

  /** Czyszczenie po udanym zapisie albo przy ponownej edycji. */
  reset() {
    this.confirmedAddress = null;
    this.status = 'idle';
    this.serverErrors = [];
  }
}
